import type { StyleSpecification } from 'maplibre-gl';

import type { AppConfig } from '@/config/appConfig.ts';

export interface BasemapDefinition {
    id: string;
    label: string;
    style: string | StyleSpecification;
    /** Path to a static thumbnail image under /map-thumbnails/. Optional — falls back to a placeholder. */
    thumbnailUrl?: string;
}

export const BASEMAP_STORAGE_KEY = 'basemap';
export const DEFAULT_BASEMAP_ID = 'ofm-liberty';

export function makeRasterStyle(tileUrl: string): StyleSpecification {
    return {
        version: 8,
        sources: {
            'raster-tiles': {
                type: 'raster',
                tiles: [tileUrl],
                tileSize: 512,
                maxzoom: 20,
            },
        },
        layers: [
            {
                id: 'raster-tiles-layer',
                type: 'raster',
                source: 'raster-tiles',
            },
        ],
    };
}

// Known, harmless console warnings from the OpenFreeMap vector styles below — these are
// upstream basemap/sprite data issues, NOT Orbit overlays, and are intentionally left alone:
//   • "Expected value to be of type number, but found null instead." — the liberty `building-3d`
//     layer uses fill-extrusion-height/-base = ["get","render_height"]/["get","render_min_height"]
//     over OSM data that contains nulls.
//   • 'Image "office"/"sports_centre"/"gate"/"bollard"/"brownfield"/… could not be loaded' — the
//     liberty poi_r1/poi_r7/poi_r20 layers set icon-image to the raw OSM `subclass`, and many
//     subclasses are absent from the published `ofm_f384` sprite.
// (Our overlay symbol layers set text-font: ['Noto Sans Regular'] to avoid glyph 404s — see the
// layer hooks. Our numeric style props are all constants or guaranteed-number expressions.)
export function getBasemapDefinitions(appConfig: AppConfig): BasemapDefinition[] {
    const basemaps: BasemapDefinition[] = [
        {
            id: 'ofm-liberty',
            label: 'Streets',
            style: 'https://tiles.openfreemap.org/styles/liberty',
            thumbnailUrl: '/map-thumbnails/streets.webp',
        },
    ];

    if (appConfig.satTileUrl) {
        basemaps.push({
            id: 'mapbox-satellite',
            label: 'Satellite',
            style: makeRasterStyle(appConfig.satTileUrl),
            thumbnailUrl: '/map-thumbnails/satellite.webp',
        });
    }

    basemaps.push(
        {
            id: 'ofm-positron',
            label: 'Light',
            style: 'https://tiles.openfreemap.org/styles/positron',
            thumbnailUrl: '/map-thumbnails/light.webp',
        },
        {
            id: 'ofm-bright',
            label: 'Outdoors',
            style: 'https://tiles.openfreemap.org/styles/bright',
            thumbnailUrl: '/map-thumbnails/outdoors.webp',
        },
    );

    if (appConfig.mapTileUrl) {
        basemaps.push({
            id: 'mapbox-map',
            label: 'Dark',
            style: makeRasterStyle(appConfig.mapTileUrl),
            thumbnailUrl: '/map-thumbnails/dark.webp',
        });
    }

    return basemaps;
}

export type MapViewMode = '2d' | '3d' | 'globe';
export const MAP_VIEW_MODE_KEY = 'map-view-mode';
export const DEFAULT_VIEW_MODE: MapViewMode = '2d';

export function resolveBasemap(basemaps: BasemapDefinition[], selectedId: string): BasemapDefinition {
    return basemaps.find((b) => b.id === selectedId) ?? basemaps[0];
}
